import { cloneDeep } from "lodash";
import { stripShortTextPresetProps } from "../textCompDisplay";

/**
 * Converts the FE preview outline into the shape the import BE expects:
 * applies existing-field mappings, title/tag-path normalisation, and strips
 * all FE-only preview fields. Pure — used by both the
 * first-import (ContentTypeTitleModal) and reimport (useReimportWithTypeChanges)
 * submit paths.
 */
export const prepareSchemaExtractorForBackend = (config) => {
  const TAG_INPUT_TYPES = ["TagsInputSingle", "TagsInputMulti"];
  const columnConfig = cloneDeep(config.outline);

  const newColumnConfig = columnConfig
    .filter((col) => col.enabled !== false)
    .map((col) => {
      // Reimport: if the user mapped this column onto an existing dataset field,
      // adopt that field's identity (type + target path) so the data routes into
      // it and the backend creates NO new field. Done before the title check so a
      // mapped column is never mistaken for the title field.
      // An unresolved "Existing Field" choice must never become a payload: it
      // would silently import as a BRAND NEW field instead of the one the user
      // was in the middle of picking. The panel blocks reaching this state and
      // validateMappings gates Confirm Import, but both are UI — this is the
      // last FE point where the intent is still visible (the markers are
      // stripped below), so it refuses outright rather than degrading.
      if (col.mappingMode === "existing" && !col.mappedField?.targetValuePath) {
        throw new Error(
          `Column "${col.sourceValuePath}" is set to map onto an existing field but none was chosen.`
        );
      }

      if (col.mappingMode === "existing" && col.mappedField) {
        col.comp = col.mappedField.comp;
        col.targetValuePath = col.mappedField.targetValuePath;
        delete col.dummyCompNameValue;
        if (col.mappedField.tagType) {
          col.tagType = col.mappedField.tagType;
        } else {
          delete col.tagType;
        }
        // The column adopts the existing field's identity — its own detected
        // Short Text preset (if any) must not persist into the stored outline
        // and misrepresent the target field.
        stripShortTextPresetProps(col);
      }

      // Handle title field setup. The title block is a plain TextInput, so a
      // Short Text preset must never ride along (belt-and-braces: the reducer
      // already strips on designation, but a restored draft could carry one).
      if (col.dummyCompNameValue === "title") {
        stripShortTextPresetProps(col);
        col.explicitBlockConfig = {
          ...(col.explicitBlockConfig || {}),
          setValuePath: "main.title",
          displayOnly: true,
          inputOnly: true,
        };
      }

      // Handle tagType -> targetValuePath transformation
      if (TAG_INPUT_TYPES.includes(col.comp)) {
        // Ensure targetValuePath starts with tags.
        if (col.targetValuePath?.startsWith("main.")) {
          col.targetValuePath = col.targetValuePath.replace("main.", "tags.");
        }
        // If tagType is specified, use it for targetValuePath
        if (col.tagType) {
          // Store original path before overwriting
          col.og_targetValuePath = col.targetValuePath;
          col.targetValuePath = `tags.${col.tagType}`;
        }
      } else {
        // For non-tag inputs, ensure targetValuePath starts with main.
        if (col.targetValuePath?.startsWith("tags.")) {
          col.targetValuePath = col.targetValuePath.replace("tags.", "main.");
        }
      }

      // NOTE: the "Include in AI Analysis" state needs no expansion here — the
      // toggle writes props.annotations.disable directly onto the item's
      // explicitBlockConfig in applyOutlineFieldChange.

      // Remove frontend-only fields
      delete col.splitCharacterType;
      delete col.tagType;
      // Snapshot of an existing field's original identity (reimport restore).
      delete col._original;
      // Detection-confidence indicator (BE-computed) + its FE ack flag —
      // preview-only, never part of the import contract.
      delete col.detectionConfidence;
      delete col.typeConfidenceAcked;
      // Transient preview-only marker from the BE (reimport: flags fields that
      // already exist in the template so the UI can lock them). Never sent back.
      delete col.isExistingTemplateField;
      // Reimport mapping overlays — already applied above; never sent back.
      delete col.mappingMode;
      delete col.mappedField;

      return col;
    });

  return { ...config, outline: newColumnConfig };
};
